import React, { useState } from 'react';
import { HiCalendar, HiLocationMarker, HiTicket, HiBookmark } from 'react-icons/hi';
import { EventListItem } from '../../../types';
import { formatEventDateRange } from '../../../utils/dateFormatters';
import { useGeneral } from '../../../contexts';
import GrayBanner from '../../ui/GrayBanner/GrayBanner';
import styles from './EventCard.module.css';

interface EventCardProps {
  event: EventListItem;
  onClick?: (event: EventListItem) => void;
  variant?: 'horizontal' | 'vertical';
  showParticipantCount?: boolean;
  showTicketStatus?: boolean;
  onBookmarkToggle?: (event: EventListItem) => void;
  isSaved?: boolean;
}

const EventCard: React.FC<EventCardProps> = ({
  event,
  onClick,
  variant = 'vertical',
  showParticipantCount = true,
  showTicketStatus = false,
  onBookmarkToggle,
  isSaved = false
}) => {
  const { language } = useGeneral();
  const { getTranslation, language: currentLanguage } = language;
  const eventTitle = (event.eventTitle || event.title || '').replace(/\\"/g, '"');
  const imageUrl = event.cover || event.bannerImage || event.image || null;
  const hasImage = !!imageUrl;
  const location = event.isOnline === "1" 
    ? getTranslation('events.onlineEvent')
    : event.locationAddress || getTranslation('events.locationTBD');
  const participantCount = event.participantsCount || event.userCount || event.user_count || 0;
  const [imageLoading, setImageLoading] = useState(hasImage);
  
  const handleClick = () => {
    if (onClick) {
      onClick(event);
    }
  };

  const handleBookmarkClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onBookmarkToggle) {
      onBookmarkToggle(event);
    }
  };

  return (
    <div 
      className={`${styles.eventCard} ${styles[variant]}`}
      onClick={handleClick}
      role="button"
      tabIndex={0}
    >
      {/* Image */}
      <div className={styles.imageContainer}>
        {hasImage ? (
          <>
            {imageLoading && <div className="image-shimmer" style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, borderRadius: 'inherit' }} />}
            <img 
              src={imageUrl} 
              alt={eventTitle}
              className={styles.eventImage}
              onLoad={() => setImageLoading(false)}
              onError={() => {
                setImageLoading(false);
              }}
            />
          </>
        ) : (
          <GrayBanner className={styles.eventImage} id={event.eventID} />
        )}
        {/* Bookmark Button */}
        {onBookmarkToggle && (
          <button
            className={`${styles.bookmarkButton} ${isSaved ? styles.bookmarkButtonSaved : ''}`}
            onClick={handleBookmarkClick}
            aria-label={isSaved ? 'Remove bookmark' : 'Save bookmark'}
          >
            {/* eslint-disable-next-line @typescript-eslint/no-unsafe-assignment */}
            {/* @ts-ignore - react-icons compatibility with React 19 */}
            <HiBookmark className={styles.bookmarkIcon} />
          </button>
        )}
      </div>
      
      {/* Bottom Section */}
      <div className={styles.bottomSection}>
        <div className={styles.eventDetails}>
          <h4 className={styles.eventTitle}>
            {eventTitle}
          </h4>
          <div className={styles.eventMeta}>
            <div className={styles.metaItem}>
              {/* eslint-disable-next-line @typescript-eslint/no-unsafe-assignment */}
              {/* @ts-ignore - react-icons compatibility with React 19 */}
              <HiCalendar size={16} />
              <span>{formatEventDateRange(event.eventStartDateTime, event.eventEndDateTime, currentLanguage)}</span>
            </div>
            <div className={styles.metaItem}>
              {/* eslint-disable-next-line @typescript-eslint/no-unsafe-assignment */}
              {/* @ts-ignore - react-icons compatibility with React 19 */}
              <HiLocationMarker size={16} />
              <span>{location}</span>
            </div>
          </div>
        </div>
      </div>
      
      {/* Participant Count - Bottom Right */}
      {showParticipantCount && participantCount > 0 && (
        <div className={styles.participantCount}>
          {/* eslint-disable-next-line @typescript-eslint/no-unsafe-assignment */}
          {/* @ts-ignore - react-icons compatibility with React 19 */}
          <HiTicket size={12} />
          <span>{participantCount}</span>
        </div>
      )}
    </div>
  );
};

export default EventCard;
